import { ApiClient } from '@vanialabs/core-api';
import { useAuthStore } from '@vanialabs/core-auth';
import { resolveAccessToken } from '../auth/authToken';
import { reportApiError } from '../monitoring/errorReporter';

// Representative authentication is tenant-host based. Using the configured
// central API origin would replace the tenant Host header with
// api.vanialabs.ir, so the backend cannot resolve app.webronix.ir. Keep
// browser requests same-origin; nginx proxies /api to vpnapi while
// preserving the tenant hostname. The configured URL remains a non-browser
// fallback for tooling.
const API_BASE_URL =
  typeof window !== 'undefined'
    ? window.location.origin
    : (import.meta.env.VITE_API_BASE_URL ?? 'https://api.vanialabs.ir');

/**
 * Guards against duplicate redirects: multiple in-flight requests can
 * all come back with 401 around the same time (e.g. several dashboard
 * widgets refetching after an idle session), and each would otherwise
 * try to clear the session and redirect independently.
 */
let isHandlingUnauthorized = false;

/**
 * Handles session invalidation triggered from outside the React tree
 * (the ApiClient callback has no access to the router). A hard
 * navigation via `window.location` is the simplest reliable option
 * here: it works regardless of where in the app the 401 originated,
 * and guarantees a clean reload of app state on the login page.
 *
 * Storage cleanup lives entirely inside `clearSession()` now — this
 * function no longer touches localStorage directly, so there's a
 * single implementation of "what does clearing a session mean."
 */
function handleUnauthorized() {
  if (isHandlingUnauthorized) return;
  if (window.location.pathname === '/login') return;
  isHandlingUnauthorized = true;

  useAuthStore.getState().clearSession();

  window.location.href = '/login';
}

export const apiClient = new ApiClient({
  baseUrl: API_BASE_URL,

  getAuthToken: () => {
    const { session } = useAuthStore.getState();

    return resolveAccessToken(session);
  },

  onUnauthorized: handleUnauthorized,

  onRequestError: (error, info) => {
    reportApiError(error, {
      endpoint: info.path,
      method: info.method,
      status: info.status,
    });
  },
});
